'use client'

import type { Control, FieldErrorsImpl, FieldValues } from 'react-hook-form'
import {
  Select as SelectComponent,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/atoms/Select'
import React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { UseFormRegister } from 'react-hook-form'
import { cn } from '@/utils/ui'
import { Error } from '../Error'
import { Width } from '../Width'
import { SelectField } from '@/payload-types'
import { Icon } from '@/ui/atoms/Icon'
import { FormLabel } from '@/ui/atoms/FormLabel'

// Server Component
const SelectPresentation: React.FC<{
  name: string
  label?: string
  description?: string
  options?: { label: string; value: string; icon?: string | null; id?: string | null }[]
  value?: string
  hasInteracted?: boolean
  selectedOption?: { label: string; value: string; icon?: string | null }
  onChange: (value: string) => void
  onOpenChange: () => void
  errors?: any
  defaultValue?: string
}> = ({
  name,
  label,
  description,
  options,
  value,
  hasInteracted,
  selectedOption,
  onChange,
  onOpenChange,
  errors,
  defaultValue,
}) => {
  return (
    <div>
      <div className="grid gap-1 mb-2">
        <FormLabel htmlFor={name} description={description}>
          {label}
        </FormLabel>
      </div>
      <SelectComponent onValueChange={onChange} value={value} onOpenChange={onOpenChange}>
        <SelectTrigger
          className={cn(
            'flex min-h-[35px] rounded-md mt-2',
            errors?.[name]
              ? 'border border-border-error bg-bg-error'
              : 'border border-border-input hover:border-border-input-hover bg-background',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2',
            'data-[state=open]:ring-2 data-[state=open]:ring-border-focus data-[state=open]:ring-offset-2',
            '!text-body-XS-regular text-text-base p-2',
          )}
        >
          <SelectValue
            placeholder={defaultValue ? defaultValue : ''}
            className="!text-body-XS-regular text-text-base placeholder:!text-text-faded"
          />
        </SelectTrigger>
        <SelectContent className="bg-bg-base [&>div:first-child]:py-5">
          {options?.map((option, index) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className={cn(
                '!text-body-XS-regular [&>span:first-child]:hidden p-2',
                'data-[highlighted]:bg-neutral-95',
                'data-[state=checked]:bg-neutral-95',
                'relative',
                index !== 0 &&
                  'before:absolute before:left-2 before:right-2 before:top-0 before:border-t before:border-border-subtle',
              )}
            >
              <div className="flex items-center gap-2">
                {option.icon && <Icon name={option.icon} />}
                {option.label}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </SelectComponent>
    </div>
  )
}

// Client Component
export const Select: React.FC<
  SelectField & {
    control: Control<FieldValues, any>
    errors: Partial<FieldErrorsImpl<{ [x: string]: any }>>
    register: UseFormRegister<FieldValues>
    showLabelAndDescription?: boolean
  }
> = ({
  name,
  control,
  defaultValue,
  icon,
  description,
  errors,
  label,
  options,
  required,
  width,
  showLabelAndDescription = true,
}) => {
  const { setValue, watch } = useFormContext()
  const value = watch(name)
  const [hasInteracted, setHasInteracted] = React.useState(false)
  const selectedOption = options?.find((option) => option.value === value)
  const defaultOption = options?.find((option) => option.value === defaultValue)

  React.useEffect(() => {
    if (defaultValue && !value) {
      setValue(name, defaultValue)
    }
  }, []) // Only run on mount

  return (
    <Width width={width ?? 100}>
      <div>
        <div className="grid gap-1 mb-2">
          <FormLabel
            htmlFor={name}
            description={description}
            useNormalFontColor={showLabelAndDescription}
          >
            {label}
          </FormLabel>
        </div>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value } }) => (
            <SelectComponent
              onValueChange={(val) => {
                setHasInteracted(true)
                onChange(val)
              }}
              value={value}
              onOpenChange={() => setHasInteracted(true)}
            >
              <SelectTrigger
                className={cn(
                  'flex min-h-[35px] rounded-md mt-2',
                  errors[name]
                    ? 'border border-border-error bg-bg-error'
                    : 'border border-border-input hover:border-border-input-hover bg-background',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2',
                  'data-[state=open]:ring-2 data-[state=open]:ring-border-focus data-[state=open]:ring-offset-2',
                  '!text-body-XS-regular',
                  showLabelAndDescription ? 'text-text-base' : 'text-text-faded',
                  'p-2',
                )}
              >
                <SelectValue
                  placeholder={defaultOption?.label ?? defaultValue ?? ''}
                  className={cn(
                    '!text-body-XS-regular',
                    showLabelAndDescription ? 'text-text-base' : 'text-text-faded',
                    'placeholder:text-text-faded',
                  )}
                />
              </SelectTrigger>
              <SelectContent className="bg-bg-base [&>div:first-child]:py-5">
                {options?.map((option, index) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className={cn(
                      '!text-body-XS-regular [&>span:first-child]:hidden p-2',
                      'data-[highlighted]:bg-neutral-95',
                      'data-[state=checked]:bg-neutral-95',
                      'relative',
                      index !== 0 &&
                        'before:absolute before:left-2 before:right-2 before:top-0 before:border-t before:border-border-subtle',
                    )}
                  >
                    <div className="flex items-center gap-2 text-text-base">
                      {option.icon && <Icon name={option.icon} />}
                      {option.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectComponent>
          )}
          rules={{ required: required ?? false }}
        />
      </div>
      {errors[name] && <Error />}
    </Width>
  )
}
